import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth.store';
import {
  isAllowedForSegment,
  normalizeInstitutionSegment,
  segmentI18nContext,
  type InstitutionSegmentContext,
  type InstitutionSegmentOrNone,
} from '@/lib/institution-segment';

export type InstitutionSegmentInfo = {
  segment: InstitutionSegmentOrNone;
  isEducation: boolean;
  isEnterprise: boolean;
  /** À passer à `t(key, { context })` : clés `key_education` en éducation, clé de base sinon. */
  context: InstitutionSegmentContext;
  /** Fonctionnalité disponible pour le segment de l’établissement (voir `lib/institution-segment.ts`). */
  isAllowed: (featureKey: string) => boolean;
};

/** Segment de l’établissement du compte connecté (source unique pour gardes, menus et vocabulaire). */
export function useInstitutionSegment(): InstitutionSegmentInfo {
  const raw = useAuthStore((s) => s.user?.institutionSegment);
  const segment = normalizeInstitutionSegment(raw);
  const isAllowed = useCallback((featureKey: string) => isAllowedForSegment(featureKey, segment), [segment]);
  return useMemo(
    () => ({
      segment,
      isEducation: segment === 'education',
      isEnterprise: segment === 'enterprise',
      context: segmentI18nContext(segment),
      isAllowed,
    }),
    [segment, isAllowed],
  );
}

type SegmentTOptions = Record<string, unknown>;

/**
 * `t()` qui applique automatiquement le contexte de segment (`key_education` en éducation, clé de base sinon).
 * Pour les écrans communs aux deux segments dont le vocabulaire diffère (classes, évaluations de classe).
 */
export function useSegmentTranslation() {
  const { t, i18n } = useTranslation();
  const { context } = useInstitutionSegment();
  const segmentT = useCallback(
    (key: string, defaultOrOptions?: string | SegmentTOptions, maybeOptions?: SegmentTOptions): string => {
      if (typeof defaultOrOptions === 'string') {
        return String(t(key, defaultOrOptions, { ...(maybeOptions ?? {}), context }));
      }
      return String(t(key, { ...(defaultOrOptions ?? {}), context }));
    },
    [t, context],
  );
  return { t: segmentT, i18n };
}
